import React, { useEffect } from 'react';
import {
  Box, Typography, Stack, Switch, TextField, Divider,
  Select, MenuItem, FormControl,
} from '@mui/material';
import { GOOGLE_FONTS, loadGoogleFont } from '../../RichTextEditor/fontUtils';
import { ColorSwatchPicker } from '../../../business/ColorSwatchPicker/ColorSwatchPicker';

const SIZE_OPTIONS = [
  { val: 'small',  label: 'קטן' },
  { val: 'medium', label: 'בינוני' },
  { val: 'large',  label: 'גדול' },
];

const SYMBOL_OPTIONS = [
  { val: '✦', label: '✦' },
  { val: '❋', label: '❋' },
  { val: '✿', label: '✿' },
  { val: '★', label: '★' },
  { val: '✡', label: '✡' },
  { val: '',   label: 'ללא' },
];

const Label = ({ children }) => (
  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#374151', mb: 0.75, display: 'block' }}>
    {children}
  </Typography>
);

const GreetingSection = ({ prefix, title, formData, onChange }) => {
  const p       = prefix.toLowerCase();
  const showKey = `show${prefix}Greeting`;
  const textKey = `${p}Greeting`;
  const bgKey   = `${p}BgColor`;
  const tcKey   = `${p}TextColor`;
  const fontKey = `${p}FontFamily`;
  const sizeKey = `${p}FontSize`;
  const symKey  = `${p}Symbol`;

  const visible = formData[showKey] ?? false;
  const fontVal = formData[fontKey] || 'inherit';

  useEffect(() => { loadGoogleFont(fontVal); }, [fontVal]);

  const handleFontChange = (val) => {
    loadGoogleFont(val);
    onChange({ [fontKey]: val });
  };

  const handleOpenFontMenu = () => {
    GOOGLE_FONTS.forEach(f => loadGoogleFont(f.value));
  };

  return (
    <Stack spacing={0}>
      {/* Header row */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={visible ? 2.5 : 0}>
        <Typography sx={{ fontWeight: 700, color: '#111827', fontSize: '0.9rem' }}>{title}</Typography>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af' }}>
            {visible ? 'מופיעה' : 'מוסתרת'}
          </Typography>
          <Switch
            checked={visible}
            onChange={(e) => onChange({ [showKey]: e.target.checked })}
            size="small"
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': { color: '#111827' },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#111827' },
            }}
          />
        </Stack>
      </Stack>

      {visible && (
        <Stack spacing={2.5}>
          {/* טקסט */}
          <Box>
            <Label>טקסט ברכה</Label>
            <TextField
              fullWidth size="small"
              value={formData[textKey] || ''}
              onChange={(e) => onChange({ [textKey]: e.target.value })}
              sx={{
                bgcolor: 'white',
                '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
              }}
            />
          </Box>

          {/* צבע רקע */}
          <Box>
            <Label>צבע רקע</Label>
            <ColorSwatchPicker
              value={formData[bgKey] || ''}
              onChange={(c) => onChange({ [bgKey]: c })}
              showDefaultOption
              size={24}
            />
          </Box>

          {/* צבע טקסט */}
          <Box>
            <Label>צבע טקסט</Label>
            <ColorSwatchPicker
              value={formData[tcKey] || ''}
              onChange={(c) => onChange({ [tcKey]: c })}
              showDefaultOption
              size={24}
            />
          </Box>

          {/* גופן */}
          <Box>
            <Label>גופן</Label>
            <FormControl fullWidth size="small">
              <Select
                value={fontVal}
                onChange={(e) => handleFontChange(e.target.value)}
                onOpen={handleOpenFontMenu}
                renderValue={(val) => {
                  const font = GOOGLE_FONTS.find(f => f.value === val);
                  return (
                    <span style={{ fontFamily: val === 'inherit' ? 'inherit' : val, fontSize: '0.85rem' }}>
                      {font?.name || val}
                    </span>
                  );
                }}
                sx={{
                  bgcolor: 'white',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
                }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
              >
                {GOOGLE_FONTS.map((font) => (
                  <MenuItem key={font.value} value={font.value} sx={{ display: 'block', py: 0.75 }}>
                    <Typography sx={{ fontSize: '0.72rem', color: '#6b7280', lineHeight: 1.2 }}>
                      {font.name}
                    </Typography>
                    <Typography sx={{
                      fontFamily: font.value === 'inherit' ? 'inherit' : font.value,
                      fontSize: '0.9rem', color: '#111827', lineHeight: 1.4,
                    }}>
                      שמחות בקהילה
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* גודל + סמלים */}
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Box sx={{ flex: 1 }}>
              <Label>גודל</Label>
              <FormControl fullWidth size="small">
                <Select
                  value={formData[sizeKey] || 'medium'}
                  onChange={(e) => onChange({ [sizeKey]: e.target.value })}
                  sx={{
                    bgcolor: 'white',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
                  }}
                >
                  {SIZE_OPTIONS.map(opt => (
                    <MenuItem key={opt.val} value={opt.val}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box>
              <Label>סמלים</Label>
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ width: 100 }}>
                {SYMBOL_OPTIONS.map(opt => {
                  const cur = formData[symKey] !== undefined ? formData[symKey] : '✦';
                  const sel = cur === opt.val;
                  return (
                    <Box
                      key={`sym-${opt.val}`}
                      onClick={() => onChange({ [symKey]: opt.val })}
                      sx={{
                        width: 28, height: 28, borderRadius: 1, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: opt.val ? '0.95em' : '0.58rem', fontWeight: 500,
                        userSelect: 'none', transition: 'all 0.15s',
                        bgcolor: sel ? '#111827' : 'white',
                        color:   sel ? 'white'   : '#6b7280',
                        border:  `1px solid ${sel ? '#111827' : '#e5e7eb'}`,
                        '&:hover': { borderColor: '#9ca3af' },
                      }}
                    >
                      {opt.val || 'ללא'}
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

const colSx = {
  flex: '1 1 0', minWidth: 0,
  p: 2.5, borderRadius: 2,
  bgcolor: '#f9fafb',
  border: '1px solid #e5e7eb',
};

const SimchaGreetingsTab = ({ formData, onChange }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', direction: 'rtl', gap: 1.5, mx: -3 }}>
    <Box sx={colSx}>
      <GreetingSection prefix="Top" title="ברכה עליונה" formData={formData} onChange={onChange} />
    </Box>
    <Box sx={colSx}>
      <GreetingSection prefix="Bottom" title="ברכה תחתונה" formData={formData} onChange={onChange} />
    </Box>
  </Box>
);

export default SimchaGreetingsTab;
